// This is used as the API slice definition and acts as our network API interface layer.
import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { AxiosError } from 'axios';

import { notify } from 'src/clients/ApiService';
import { AppAPIName, PortalConfigEntity, UnAuthAPIName } from 'src/constants';
import {
  PortalAssistantSettings,
  PortalAuthSettings,
  PortalConfig,
} from 'src/entities/PortalConfig';
import {
  CreateStripeExpressAccountRequest,
  CreateStripeExpressAccountResponse,
} from 'src/services/api/unauthApi';
import { API } from 'src/utils/AmplifyApiUtils';
import { ensureUnreachable, isRecord } from 'src/utils/common_utils';
import {
  traceIdFromTraceparent,
  TRACEPARENT_HEADER,
} from 'src/utils/traceparent';

import {
  guardEndpointMap,
  guardInjectedLifecycles,
} from './guardQueryLifecycle';

export enum ApiTags {
  domains = 'Domains',
  domains_emails = 'Domain Emails',
  recommended_resources = 'Recommended Resources',
  latest_releases = 'Latest Releases',
  notifications_settings = 'Notifications Settings',
  payment_fees = 'Payment Fees',
  payment_links = 'Payment Links',
  referrals = 'Referrals',
  importer = 'Importer',
  forms = 'Forms',
  form_responses = 'Form Responses',
  form_detail = 'Form Detail',
  templates = 'Templates',
  inbox_notifications = 'Inbox Notifications',
  users = 'Users',
  companies = 'Companies',
  // this fetches clients using rtk-query in its own endpoint.
  // Companies uses the larger get user data endpoint that should be broken up
  clientCompanies = 'Client Companies',
  fileChannels = 'File Channels',
  otherUserData = 'Other User Data',
  userPermissions = 'User Permissions',
  settings = 'Settings',
  onboarding_task_status = 'Onboarding Task Status',
  app_visibility = 'App Visibility',
  portal_config_settings = 'Portal Config Settings',
  contentfulApps = 'Contentful Apps',
  contract_templates = 'Contract templates',
  contracts = 'Contracts',
  automations = 'Automations',
  automationTemplates = 'Automation Templates',
  automationTriggers = 'Automation Triggers',
  automationActions = 'Automation Actions',
  apiKeys = 'ApiKeys',
  webhookConfigSets = 'Webhook Config Sets',
  apps = 'Apps',
  clientCompanyVisibility = 'Client Company Visibility',
  clientUsers = 'Client Users',
  products = 'Products',
  prices = 'Prices',
  invoices = 'Invoices',
  clientInvoices = 'Client Invoices',
  refunds = 'Refunds',
  appConnections = 'App Connections',
  appBuilders = 'App Builders',
  appBuilderIntegrations = 'App Builder Integrations',
  appBuilderEvents = 'App Builder Events',
  builderSessions = 'Builder Sessions',
  qaSessions = 'QA Sessions',
  qaRuns = 'QA Runs',
  googlemaps = 'Google Maps',
  analyticsClients = 'Analytics Clients',
  analyticsActiveClients = 'Analytics Active Clients',
  analyticsSubscriptions = 'Analytics Subscriptions',
  analyticsRecentlyVisited = 'Analytics Recently Visited',
  customFields = 'Custom Fields',
  tableProperties = 'Table Properties',
  subscriptions = 'Subscriptions',
  stripeCustomer = 'Stripe Customer',
  paymentMethods = 'Payment Methods',
  bankAccounts = 'Bank Accounts',
  notes = 'Notes',
  permissions = 'Permissions',
  clientSubscription = 'Client Subscriptions',
  auditLogs = 'Audit Logs',
  mcpConnections = 'MCP Connections',
  contextItems = 'Context Items',
  portalConnections = 'Portal Connections',
  portalConnectionsAvailable = 'Portal Connections Available',
  personalConnections = 'Personal Connections',
  assistantPrincipal = 'Assistant Principal',
  studioPlans = 'Studio Plans',
  creditBalance = 'Credit Balance',
  currentPlan = 'Current Plan',
  churnAccess = 'Churn Access',
  creditUsageSummary = 'Credit Usage Summary',
  studioAppUsage = 'Studio App Usage',
}

export enum ApiMethods {
  get = 'get',
  post = 'post',
  put = 'put',
  patch = 'patch',
  del = 'del',
}

export interface ApiError {
  error?: {
    data?: {
      message: string;
    };
  };
}

const getDataMessage = (container: unknown): string | undefined => {
  if (!isRecord(container)) {
    return undefined;
  }

  const dataValue = container.data;
  if (!isRecord(dataValue)) {
    return undefined;
  }

  const { message } = dataValue;
  return typeof message === 'string' ? message : undefined;
};

export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (!isRecord(error)) {
    return undefined;
  }

  return (
    getDataMessage(error.error) ??
    getDataMessage(error.response) ??
    getDataMessage(error)
  );
};

const REQUEST_ID_HEADER = 'x-request-id';

// The backend uses the browser's trace id as the request id, so the outgoing
// traceparent is a fallback when the response header isn't readable (CORS,
// or no response at all).
const getRequestId = (err: AxiosError): string | undefined => {
  const fromHeader = err.response?.headers?.[REQUEST_ID_HEADER];
  if (typeof fromHeader === 'string' && fromHeader) {
    return fromHeader;
  }
  const traceparent = err.config?.headers?.[TRACEPARENT_HEADER];
  return typeof traceparent === 'string'
    ? traceIdFromTraceparent(traceparent)
    : undefined;
};

export const amplifyBaseQuery =
  (
    { apiName }: { apiName: string } = { apiName: 'AppAPI' },
  ): BaseQueryFn<
    {
      path: string;
      method: ApiMethods;
      options: {
        [key: string]: any;
      };
    },
    unknown,
    unknown
  > =>
  async ({ path, method, options }) => {
    try {
      switch (method) {
        case ApiMethods.get: {
          const result = await API.get(apiName, path, options);
          return { data: result };
        }
        case ApiMethods.post: {
          const result = await API.post(apiName, path, options);
          return { data: result };
        }
        case ApiMethods.del: {
          const result = await API.del(apiName, path, options);
          return { data: result };
        }
        case ApiMethods.put: {
          const result = await API.put(apiName, path, options);
          return { data: result };
        }
        case ApiMethods.patch: {
          const result = await API.patch(apiName, path, options);
          return { data: result };
        }
        default:
          return ensureUnreachable(method);
      }
    } catch (axiosError) {
      const err = axiosError as AxiosError;
      return {
        error: {
          status: err.response?.status,
          data: err.response?.data,
          requestId: getRequestId(err),
        },
      };
    }
  };

type CloenableStatusResponse = {
  isCloneable: boolean;
};
// How long an unsubscribed builder-view row (app builder, its sessions list)
// stays cached. RTK Query's 60s default meant returning to an app after a
// minute dropped the whole builder workspace to the page skeleton while the
// row refetched; within this window the switch swaps content in place instead
// (paired with refetchOnMountOrArgChange at the call sites, so a revisit still
// refreshes in the background).
export const BUILDER_VIEW_CACHE_SECONDS = 600;

// How stale a cached builder-view row may be before a revisit refetches it in
// the background. Pairs with the cache window above: the cache keeps the row
// alive so a switch never drops to the page skeleton, this keeps what it shows
// honest without refetching rows fetched moments ago.
export const BUILDER_VIEW_REFETCH_AFTER_SECONDS = 30;

export const appAPI = guardInjectedLifecycles(
  createApi({
    baseQuery: amplifyBaseQuery({ apiName: 'AppAPI' }),
    reducerPath: 'appAPI',
    tagTypes: Object.values(ApiTags),
    endpoints: (build) =>
      guardEndpointMap({
        getPortalConfig: build.query<PortalConfig, void>({
          query: () => ({
            path: '/entities/config',
            method: ApiMethods.get,
            options: {},
          }),
          providesTags: [ApiTags.portal_config_settings],
        }),
        getPortalAuthSettings: build.query<PortalAuthSettings, void>({
          query: () => ({
            path: '/portal/auth-settings',
            method: ApiMethods.get,
            options: {},
          }),
          providesTags: [ApiTags.portal_config_settings],
        }),
        getPortalAssistantSettings: build.query<PortalAssistantSettings, void>({
          query: () => ({
            path: '/portal/assistant-settings',
            method: ApiMethods.get,
            options: {},
          }),
          providesTags: [ApiTags.portal_config_settings],
        }),
        savePortalAssistantSettings: build.mutation<
          PortalAssistantSettings,
          PortalAssistantSettings
        >({
          query: (body) => ({
            path: '/portal/assistant-settings',
            method: ApiMethods.patch,
            options: {
              body,
            },
          }),
          invalidatesTags: [ApiTags.portal_config_settings],
        }),
        savePortalAuthSettings: build.mutation<
          PortalAuthSettings,
          PortalAuthSettings
        >({
          query: (body) => ({
            path: '/portal/auth-settings',
            method: ApiMethods.patch,
            options: {
              body,
            },
          }),
          invalidatesTags: [ApiTags.portal_config_settings],
        }),
        getCloneableStatus: build.query<CloenableStatusResponse, string>({
          query: (portalId: string) => ({
            path: `/portal/${portalId}/cloneable`,
            method: ApiMethods.get,
            options: {},
          }),
        }),
        savePortalConfig: build.mutation<
          PortalConfigEntity,
          Partial<PortalConfigEntity> & Pick<PortalConfig, 'id'>
        >({
          query: (body) => ({
            path: '/entities/config',
            method: ApiMethods.post,
            options: {
              body,
            },
          }),
          invalidatesTags: [ApiTags.portal_config_settings],
          async onQueryStarted(_, { dispatch, queryFulfilled }) {
            try {
              await queryFulfilled;
              notify({
                status: 'success',
                successMessage: 'Workspace settings updated.',
                dispatch,
              });
            } catch (error) {
              notify({
                status: 'error',
                errorMessage: 'Workspace settings could not be updated',
                error,
                dispatch,
              });
            }
          },
        }),

        getPortalPlan: build.query({
          queryFn: async ({ isAuthScreen }) => {
            const path = `/portal/any/plan`;
            const result = await API.get(
              isAuthScreen ? AppAPIName : UnAuthAPIName,
              path,
              {},
            );
            return { data: result };
          },
        }),
        createStripeExpressAccount: build.mutation<
          CreateStripeExpressAccountResponse,
          CreateStripeExpressAccountRequest
        >({
          query: (input) => ({
            path: '/users/stripe/express-account',
            method: ApiMethods.post,
            options: {
              body: {
                ...input,
              },
            },
          }),
        }),
      }),
  }),
);

export const {
  useGetPortalConfigQuery,
  useSavePortalConfigMutation,
  useGetCloneableStatusQuery,
  useGetPortalPlanQuery,
  useCreateStripeExpressAccountMutation,
  endpoints,
  useGetPortalAuthSettingsQuery,
  useSavePortalAuthSettingsMutation,
  useGetPortalAssistantSettingsQuery,
  useSavePortalAssistantSettingsMutation,
} = appAPI;
